import { useCallback, useEffect, useMemo, useState } from 'react';

interface IUseListSelectionArgs<T> {
  items: T[];
  /** Stable id accessor for an item (wrap in useCallback). */
  getId: (item: T) => string;
  /** Whether an item may be selected, e.g. available && not already sent (wrap in useCallback). */
  isSelectable: (item: T) => boolean;
  /** Mirrors the current selection up to the parent whenever it changes. */
  onSelectionChange?: (ids: string[]) => void;
}

/**
 * Generic multi-select state for a flat list: owns the selected Set, derives the
 * selectable ids and the "all selected" flag, and exposes toggle + select-all.
 * Mirrors the current selection up via `onSelectionChange`. Shared so list
 * surfaces (candidate list now, invite list later) don't each re-implement the
 * same selection bookkeeping.
 */
export const useListSelection = <T>({
  items,
  getId,
  isSelectable,
  onSelectionChange,
}: IUseListSelectionArgs<T>) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Mirror selection up to the parent (single source of truth = this Set). Keyed
  // on the sorted ids so a new Set with the same contents doesn't re-fire.
  const selectedKey = [...selected].sort().join(',');
  useEffect(() => {
    onSelectionChange?.([...selected]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const selectableIds = useMemo(
    () => items.filter(isSelectable).map(getId),
    [items, isSelectable, getId],
  );

  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggle = useCallback((ids: string[], forceAdd?: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (forceAdd === undefined) {
          if (next.has(id)) {
            next.delete(id);
          } else {
            next.add(id);
          }
        } else if (forceAdd) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    toggle(selectableIds, !allSelected);
  }, [toggle, selectableIds, allSelected]);

  return { selected, selectableIds, allSelected, toggle, toggleAll };
};
